import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import { useAtom } from 'jotai'
import styled from 'styled-components'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
  profileVerificationProgressAtom,
  ProfileVerificationProgressState,
  useTwitterSignature,
  useVerifyTweet,
} from '../../atoms/profile'
import { Input, useToasts } from '@geist-ui/react'
import { X, ChevronLeft } from '@geist-ui/react-icons'
import { useEffect } from 'react'

// ========================== React component ==================================

const VerificationDialog = (): JSX.Element => {
  const [progressState, setProgressState] = useAtom(profileVerificationProgressAtom)
  const { signature, signMessage } = useTwitterSignature()
  const [link, setLink] = React.useState('')
  const verify = useVerifyTweet(link)
  const [, setToast] = useToasts()

  const fullscreen = useMediaQuery('(max-width: 43em)')
  const isTablet = useMediaQuery('(max-width: 62em)')

  const tweetSignature = (): void => {
    const tweet = `Claiming my profile on @GlassProtocol\n\n${signature}\n\nhttp://glass.xyz/`
    const url = 'https://twitter.com/intent/tweet?text=' + encodeURI(tweet)
    window.open(url)
    setProgressState(ProfileVerificationProgressState.PASTING_TWEET_URL)
  }

  useEffect(() => {
    if (progressState === ProfileVerificationProgressState.ERROR) {
      setToast({ type: 'error', text: 'Something went wrong. Can you try again?' })
    } else if (progressState === ProfileVerificationProgressState.SUCCESS) {
      setToast({ text: 'You just verified your profile!' })
    }
  }, [progressState])

  return (
    <Dialog
      onClose={() => {
        console.log('Closed')
      }}
      aria-labelledby="profile-verification-dialog"
      open={
        progressState !== ProfileVerificationProgressState.INACTIVE &&
        progressState !== ProfileVerificationProgressState.ERROR &&
        progressState !== ProfileVerificationProgressState.SUCCESS
      }
      fullScreen={fullscreen}
      PaperProps={{
        style: {
          backgroundColor: '#101010',
          borderRadius: fullscreen ? 0 : 30,
          minWidth: isTablet ? '75vw' : '75vh',
          minHeight: isTablet ? '75vw' : '40vh',
          height: !fullscreen ? (isTablet ? '75vw' : '60vh') : '',
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <OuterDiv>
        {progressState > ProfileVerificationProgressState.SIGN_MESSAGE && (
          <GoBackButton
            color="white"
            size={'35px'}
            onClick={() => setProgressState(progressState - 1)}
          />
        )}
        <CloseButton
          color="white"
          size={'35px'}
          onClick={() => setProgressState(ProfileVerificationProgressState.INACTIVE)}
        />
        <Title>Verify your profile</Title>
        <Subtitle>Show the Glass community that you&apos;re a real person.</Subtitle>
        {/*Step one fragment: UI for posting the tweet*/}
        {progressState === ProfileVerificationProgressState.SIGN_MESSAGE && (
          <>
            <Header>Step one</Header>
            <Description>Sign a message to create your identifier.</Description>
            <Spacer />
            <VerifyButton disabled={false} onClick={signMessage}>
              Sign
            </VerifyButton>
          </>
        )}
        {/*Step two fragment: UI for posting the tweet*/}
        {progressState === ProfileVerificationProgressState.POSTING_TWEET && (
          <>
            <Header>Step two</Header>
            <Description>Post a tweet with this unique identifier:</Description>
            <UniqueIdentifier>{signature}</UniqueIdentifier>
            <Spacer />
            <VerifyButton disabled={false} onClick={tweetSignature}>
              Tweet
            </VerifyButton>
          </>
        )}
        {/*Step three fragment: UI for verifying the posted tweet*/}
        {progressState === ProfileVerificationProgressState.PASTING_TWEET_URL && (
          <>
            <Header>Step three</Header>
            <Description>Paste the URL of the tweet you just posted</Description>
            <Textfield
              placeholder="twitter.com/username/status/1234"
              enterKeyHint={undefined}
              width="100%"
              scale={4 / 3}
              onChange={(e) => setLink(e.target.value)}
            />
            <Spacer />
            <VerifyButton disabled={link.trim() === ''} onClick={verify}>
              Verify
            </VerifyButton>
          </>
        )}
      </OuterDiv>
    </Dialog>
  )
}

export default VerificationDialog

// ========================== Styled components ================================

const OuterDiv = styled.div`
  width: 80%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: auto;
  margin-left: auto;
`

const Title = styled.div`
  color: white;
  font-size: 48px;
  font-weight: 500;
  text-align: center;
  padding: 25px;
  padding-bottom: 0;
`

const Subtitle = styled.div`
  padding: 5px 25px;
  color: white;
  font-size: 20px;
  font-weight: 400;
  text-align: center;
  white-space: pre-line;
  margin-bottom: 30px;
`

const Header = styled.div`
  color: white;
  font-size: 25px;
  font-weight: 500;
  margin-right: auto;
  padding-bottom: 15px;
`

const Description = styled.div`
  color: white;
  font-size: 17px;
  font-weight: 400;
  margin-right: auto;
  margin-bottom: 5px;
`

const UniqueIdentifier = styled.div`
  max-width: 100%;
  color: white;
  font-size: 17px;
  font-weight: 600;
  margin-right: auto;
  word-wrap: break-word;
  margin-bottom: 5px;
`

const Spacer = styled.div`
  height: 30px;
`

const VerifyButton = styled.div<{ disabled: boolean }>`
  background-color: ${(props) => (props.disabled ? 'grey' : 'white')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};
  color: black;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  border-radius: 14px;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;

  @media (min-width: 43em) {
    font-size: 18px;
    border-radius: 14px;
  }
`

const Textfield = styled(Input)`
  margin-top: 10px;
`

const CloseButton = styled(X)`
  right: 4%;
  top: 7%;
  position: absolute;
`

const GoBackButton = styled(ChevronLeft)`
  left: 4%;
  top: 7%;
  position: absolute;
`
